import { useState, type CSSProperties } from "react";
import type { RecipeIngredient } from "../types/drink";
import { renderLine, scaleLine } from "../lib/scaling";
import FlagIngredientModal from "./FlagIngredientModal";

interface Props {
  ingredients: RecipeIngredient[];
  cocktailCount: number;
  /** Map of recipe_ingredient.id → checked. Defaults to empty (unchecked). */
  checked: Record<string, boolean>;
  onToggle: (ingredientId: string) => void;
  /** Surfaces a flash message when an ingredient is flagged out (parent decides
   * how to display — toast, snackbar, etc.). Optional; if omitted the list
   * falls back to a local console log. */
  onFlashMessage?: (msg: string) => void;
  /** When ingredient is flagged out, the parent may want to refresh the
   * underlying drink data (since available=false changes the row appearance).
   * Optional. */
  onIngredientFlagged?: () => void;
}

/**
 * Tap-anywhere-on-row ingredient list with scaling-aware amount + checkoff.
 *
 * - The row is a wrapping <li> (NOT a <button>) so it can contain both the
 *   big tap-to-check button AND a small "86" report button without nesting
 *   buttons (invalid HTML; see L30).
 * - Checkoff button fills most of the row width; "86" button sits at the
 *   right when the ingredient is currently available. When already
 *   unavailable, no 86 button — the row already shows the unavailable flag.
 * - "86" is bar-industry shorthand for "out of stock / cannot serve".
 *   Tapping it opens FlagIngredientModal which confirms + sends SMS to
 *   bar_owners and bar_managers.
 * - batch_only ingredients (per scale_type) are hidden entirely.
 */
export default function IngredientList({
  ingredients,
  cocktailCount,
  checked,
  onToggle,
  onFlashMessage,
  onIngredientFlagged,
}: Props) {
  const [flagging, setFlagging] = useState<{ id: string; name: string } | null>(null);

  return (
    <>
      <ul style={styles.list}>
        {ingredients.map((ing) => {
          const line = scaleLine(ing.amount, ing.unit, ing.scale_type, cocktailCount);
          if (line.hidden) return null;

          const isChecked = !!checked[ing.id];
          const isUntracked = !!ing.ingredient.is_untracked;
          const unavailable = !isUntracked && ing.ingredient.available === false;
          // The 86 endpoint accepts master ingredient IDs, not catalog/menu IDs.
          const showFlagBtn = !unavailable && !isUntracked && !ing.menu_item_id && !ing.catalog_item_id;

          return (
            <li key={ing.id} style={styles.rowWrap}>
              <button
                type="button"
                onClick={() => onToggle(ing.id)}
                aria-pressed={isChecked}
                style={{
                  ...styles.row,
                  ...(isChecked ? styles.rowChecked : {}),
                  ...(unavailable ? styles.rowUnavailable : {}),
                  ...(showFlagBtn ? { paddingRight: 56 } : {}), // leave space for the "86" button only if present
                }}
              >
                <span
                  style={{
                    ...styles.checkCircle,
                    ...(isChecked ? styles.checkCircleActive : {}),
                    ...(unavailable && !isChecked ? styles.checkCircleUnavail : {}),
                  }}
                  aria-hidden
                >
                  {isChecked ? "✓" : unavailable ? "!" : ""}
                </span>
                <span
                  style={{
                    ...styles.amount,
                    ...(isChecked ? styles.amountChecked : {}),
                  }}
                >
                  {renderLine(line)}
                </span>
                <span
                  style={{
                    ...styles.name,
                    ...(isChecked ? styles.nameChecked : {}),
                    ...(unavailable ? styles.nameUnavail : {}),
                  }}
                >
                  {ing.display_name}
                  {unavailable && (
                    <span style={styles.unavailFlag}> · unavailable</span>
                  )}
                </span>
              </button>
              {showFlagBtn && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlagging({
                      id: ing.ingredient.id,
                      name: ing.display_name || ing.ingredient.name || "(ingredient)",
                    });
                  }}
                  aria-label={`86 ${ing.display_name || ing.ingredient.name} (mark out of stock)`}
                  title="86 it (mark out of stock)"
                  style={styles.flagBtn}
                >
                  86
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {flagging && (
        <FlagIngredientModal
          open={!!flagging}
          ingredientId={flagging.id}
          ingredientName={flagging.name}
          onClose={() => setFlagging(null)}
          onFlagged={(msg) => {
            onFlashMessage?.(msg);
            onIngredientFlagged?.();
          }}
        />
      )}
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  list: { listStyle: "none", padding: 0, margin: 0 },
  rowWrap: {
    position: "relative",
  },
  flagBtn: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: 6,
    minWidth: 44,
    minHeight: 32,
    padding: "4px 10px",
    background: "transparent",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  row: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "32px auto 1fr",
    columnGap: 10,
    alignItems: "center",
    minHeight: 48,
    padding: "8px 6px",
    border: 0,
    borderBottom: "1px solid var(--border)",
    background: "transparent",
    color: "var(--text)",
    cursor: "pointer",
    textAlign: "left",
    WebkitTapHighlightColor: "transparent",
  },
  rowChecked: {
    opacity: 0.55,
    background: "var(--bg)",
  },
  rowUnavailable: {
    background: "rgba(224,80,64,0.08)",
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 999,
    border: "2px solid var(--border)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 900,
    background: "var(--surface)",
    color: "var(--text)",
  },
  checkCircleActive: {
    background: "var(--gold)",
    borderColor: "var(--gold)",
    color: "#0D0B08",
  },
  checkCircleUnavail: {
    background: "rgba(224,80,64,0.16)",
    borderColor: "rgba(224,80,64,0.5)",
    color: "#FF8A7A",
  },
  amount: {
    fontSize: 16,
    fontWeight: 900,
    color: "var(--text)",
  },
  amountChecked: {
    textDecoration: "line-through",
  },
  name: {
    fontSize: 15,
    fontWeight: 600,
    color: "var(--text)",
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  nameChecked: {
    textDecoration: "line-through",
  },
  nameUnavail: {
    color: "#FF8A7A",
  },
  unavailFlag: {
    fontSize: 11,
    fontWeight: 700,
    color: "#FF8A7A",
  },
};
